'use client';

import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * Hand-placed architecture diagram in the site's own language: rounded-lg
 * node cards, hairline edges, mono labels, no colour. Coordinates live in a
 * 100x100 space; edges are polylines between hand-chosen points, drawn in on
 * scroll and carrying a small pulse that shows direction of flow. Everything
 * respects reduced motion (edges render complete, pulses hidden).
 */

export type ArchNode = {
  id: string;
  /** Centre position, percent of the canvas. */
  x: number;
  y: number;
  title: string;
  sub?: string;
  /** Muted nodes read as context, solid nodes as the subject. */
  emphasis?: 'solid' | 'default' | 'muted';
  /** Width in percent (default 22). */
  w?: number;
};

export type ArchEdge = {
  id: string;
  /** Polyline points in canvas percent, first = source, last = target. */
  points: [number, number][];
  label?: string;
  /** Label anchor, percent. Defaults to the midpoint of the polyline. */
  labelAt?: [number, number];
  dashed?: boolean;
  /** Animate a pulse travelling source to target. */
  pulse?: boolean;
  /** Render struck through: the path this change removed. */
  removed?: boolean;
};

export function ArchDiagram({
  nodes,
  edges,
  /**
   * Tailwind padding-bottom classes controlling canvas height, responsive so
   * the same coordinates get more vertical room on small screens.
   */
  heightClass = 'pb-[120%] sm:pb-[62%]',
  className,
  caption,
}: {
  nodes: ArchNode[];
  edges: ArchEdge[];
  heightClass?: string;
  className?: string;
  caption?: string;
}) {
  const prefersReduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const [inView, setInView] = useState(false);

  // useReducedMotion() is null on the server and the real boolean on the
  // client's first render; gating on mounted keeps hydration byte-identical.
  useEffect(() => {
    setMounted(true);
  }, []);
  const reduceMotion = mounted && !!prefersReduced;

  // `shown` latches the reveal; `inView` stays live so the infinite pulse
  // animations stop costing frames once the diagram scrolls away.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setShown(true);
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const visible = shown || !!reduceMotion;

  return (
    <figure ref={ref} className={cn('m-0', className)}>
      <div
        className={cn('relative w-full', heightClass)}
        role="img"
        aria-label={caption}
      >
        {/* Edges: decorative; the wrapping figure carries the aria-label. */}
        {/* biome-ignore lint/a11y/noSvgWithoutTitle: aria-hidden decorative layer inside a labelled role=img container */}
        <svg
          aria-hidden
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {edges.map((edge, index) => {
            const d = edge.points
              .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`)
              .join(' ');
            return (
              <motion.path
                key={edge.id}
                d={d}
                fill="none"
                vectorEffect="non-scaling-stroke"
                className={cn(
                  edge.removed
                    ? 'stroke-muted-foreground/45'
                    : 'stroke-muted-foreground/70'
                )}
                strokeWidth={1}
                strokeDasharray={
                  edge.dashed || edge.removed ? '4 4' : undefined
                }
                initial={reduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: visible ? 1 : 0 }}
                transition={{
                  duration: 0.5,
                  delay: reduceMotion ? 0 : 0.2 + index * 0.07,
                  ease: 'easeOut',
                }}
              />
            );
          })}
        </svg>

        {/* Pulses: direction of flow. Hidden under reduced motion. */}
        {!reduceMotion &&
          inView &&
          edges
            .filter((edge) => edge.pulse && !edge.removed)
            .map((edge, index) => (
              <motion.span
                key={`pulse-${edge.id}`}
                aria-hidden
                className="absolute size-1.5 rounded-full bg-foreground"
                style={{ marginLeft: -3, marginTop: -3 }}
                animate={{
                  left: edge.points.map(([x]) => `${x}%`),
                  top: edge.points.map(([, y]) => `${y}%`),
                  opacity: [0, 1, 1, 0],
                }}
                transition={{
                  duration: 2.2,
                  delay: index * 0.7,
                  repeat: Number.POSITIVE_INFINITY,
                  repeatDelay: 1.4,
                  ease: 'linear',
                }}
              />
            ))}

        {/* Edge labels */}
        {edges
          .filter((edge) => edge.label)
          .map((edge) => {
            // Fallback: geometric midpoint of the middle segment, so a
            // two-point edge labels its centre rather than its source.
            const mid = Math.max(0, Math.ceil(edge.points.length / 2) - 1);
            const [ax, ay] = edge.points[mid];
            const [bx, by] =
              edge.points[Math.min(mid + 1, edge.points.length - 1)];
            const [lx, ly] = edge.labelAt ?? [(ax + bx) / 2, (ay + by) / 2];
            return (
              <span
                key={`label-${edge.id}`}
                className={cn(
                  'absolute -translate-x-1/2 -translate-y-1/2 bg-background px-1 text-center font-mono text-[9px] leading-tight tracking-wide max-sm:max-w-28 sm:text-[10px] sm:whitespace-nowrap',
                  edge.removed
                    ? 'text-muted-foreground line-through'
                    : 'text-muted-foreground'
                )}
                style={{ left: `${lx}%`, top: `${ly}%` }}
              >
                {edge.label}
              </span>
            );
          })}

        {/* Nodes */}
        {nodes.map((node, index) => (
          <motion.div
            key={node.id}
            className={cn(
              'absolute -translate-x-1/2 -translate-y-1/2 rounded-lg border px-1.5 py-1 text-center sm:px-2.5 sm:py-1.5',
              node.emphasis === 'solid' &&
                'border-foreground bg-foreground text-background',
              (node.emphasis ?? 'default') === 'default' &&
                'border-edge bg-background',
              node.emphasis === 'muted' &&
                'border-dashed border-edge bg-background text-muted-foreground'
            )}
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              minWidth: `${node.w ?? 0}%`,
            }}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.92 }}
            animate={{ opacity: visible ? 1 : 0, scale: visible ? 1 : 0.92 }}
            transition={{
              duration: 0.35,
              delay: reduceMotion ? 0 : index * 0.06,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <span className="block font-mono text-[9px] font-semibold tracking-wide sm:text-[11px]">
              {node.title}
            </span>
            {node.sub && (
              <span
                className={cn(
                  'block font-mono text-[8px] leading-tight sm:text-[9px]',
                  node.emphasis === 'solid'
                    ? 'text-background/70'
                    : 'text-muted-foreground'
                )}
              >
                {node.sub}
              </span>
            )}
          </motion.div>
        ))}
      </div>

      {caption && (
        <figcaption className="mt-2 font-mono text-[11px] text-muted-foreground">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
